import { useEffect, useState } from "react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CategoriesSubnav from "@/components/CategoriesSubnav";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { ArrowRight, CircleCheck, Recycle, Leaf, BadgePercent, Trash2 } from "lucide-react";

const PRODUCT_SLUGS = [
  "carton-standard-reconditionne",
  "carton-livres-reconditionne",
];

const CartonsReconditionnes = () => {
  useDocumentTitle(
    "Cartons reconditionnés Paris Île-de-France | Cartons de déménagement seconde vie -40%",
    "Achetez vos cartons de déménagement reconditionnés à -40 %. Cartons standards et cartons livres déjà utilisés une fois, contrôlés et remis en vente. Solution économique et écologique en Île-de-France."
  );

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts()
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  const reconditionnes = PRODUCT_SLUGS.map((s) => products.find((p) => p.slug === s)).filter(Boolean);

  return (
    <div data-testid="cartons-reconditionnes-page">
      <CategoriesSubnav />

      {/* TOP — H1 + 2 produits + intro */}
      <section className="section-pad bg-white" id="produits">
        <div className="container-cdm">
          <div className="max-w-3xl mx-auto text-center">
            <div className="overline justify-center">Boutique cartonmove</div>
            <h1 className="mt-3 text-[28px] sm:text-[33px] md:text-[48px] font-extrabold text-[#0f1d2b] leading-tight">
              Cartons de déménagement reconditionnés
            </h1>
          </div>

          {loading ? (
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="aspect-square bg-zinc-100 animate-pulse rounded-2xl" />
              ))}
            </div>
          ) : (
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto" data-testid="reconditionnes-grid">
              {reconditionnes.map((p, idx) => (
                <ProductCard key={p.id} product={p} index={idx} hideQty ctaLabel="Acheter" />
              ))}
            </div>
          )}

          <div className="mt-12 max-w-3xl mx-auto text-center">
            <p className="text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Découvrez nos <strong>cartons de déménagement reconditionnés</strong>, récupérés après
              une première utilisation puis contrôlés par nos équipes. Solides, pratiques et proposés
              jusqu'à <strong>-40 %</strong> par rapport au neuf, ils offrent une solution{" "}
              <strong>économique et responsable</strong> pour préparer votre déménagement à Paris, en
              Île-de-France, en appartement, maison ou bureau. Une seconde vie utile pour des cartons
              encore résistants.
            </p>
          </div>

          {/* CTA Big Déménagement */}
          <div className="mt-12">
            <BigDemenageurCTA testId="reconditionnes-bigdem-cta" />
          </div>

          {/* LIVRAISON IDF */}
          <div className="mt-12 max-w-4xl mx-auto bg-white border border-[#e6edf3] rounded-2xl p-6 sm:p-8 lg:p-10">
            <div className="overline">Service de livraison</div>
            <h2 className="mt-3 text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Livraison en Île-de-France
            </h2>
            <p className="mt-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Nous livrons directement vos cartons reconditionnés avec notre propre société,
              uniquement en Île-de-France.
            </p>
            <p className="mt-3 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              Pour vous éviter d'attendre toute la journée, nous convenons ensemble d'un créneau de
              livraison adapté à vos disponibilités. Vous recevez votre commande à l'heure prévue,
              simplement et sans perte de temps.
            </p>

            <h3 className="mt-6 text-base font-extrabold text-[#0f1d2b]">Points forts :</h3>
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5" data-testid="reconditionnes-livraison-highlights">
              {[
                "Livraison par notre propre équipe",
                "Service disponible en Île-de-France",
                "Créneau horaire défini avec le client",
                "Cartons livrés directement à domicile ou en entreprise",
              ].map((h) => (
                <li key={h} className="flex items-start gap-2.5 text-[15px] text-[#0f1d2b] leading-relaxed">
                  <CircleCheck className="h-5 w-5 mt-0.5 shrink-0 text-[#237cb5]" />
                  <span>{h}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SEO sections */}
      <section className="section-pad bg-[#EEF3F7] border-t border-[#e6edf3]">
        <div className="container-cdm max-w-4xl space-y-10">
          {/* Section 1 : geste utile pour budget et environnement */}
          <article>
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Cartons reconditionnés&nbsp;: un geste utile pour votre budget et l'environnement
            </h2>
            <div className="mt-5 space-y-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              <p>
                Après un déménagement, certains cartons sont encore en très bon état. Au lieu de les
                jeter, nous les récupérons chez nos clients, les contrôlons, puis nous les remettons
                en vente à prix réduit.
              </p>
              <p>
                Ces cartons déjà utilisés une fois peuvent être proposés jusqu'à <strong>-40 %</strong>,
                afin d'aider nos clients à réduire le coût de leurs fournitures de déménagement.
              </p>
              <p>
                Notre objectif n'est pas de gagner de l'argent sur ces cartons, mais de créer un
                système plus responsable&nbsp;: moins de gaspillage, moins de déchets, et une solution
                pratique pour les clients qui souhaitent se débarrasser de leurs cartons après leur
                déménagement.
              </p>
              <p>
                Après une deuxième utilisation, les cartons sont récupérés puis déposés en déchetterie
                ou dans une filière adaptée, à notre charge.
              </p>
            </div>

            <h3 className="mt-8 text-base font-extrabold text-[#0f1d2b]">Ce service permet de&nbsp;:</h3>
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Recycle, text: "Donner une seconde vie aux cartons" },
                { icon: BadgePercent, text: "Réduire le prix des fournitures pour les clients" },
                { icon: Trash2, text: "Aider les clients à se débarrasser de leurs cartons après déménagement" },
                { icon: Leaf, text: "Limiter les déchets et protéger l'environnement" },
              ].map(({ icon: Icon, text }) => (
                <li
                  key={text}
                  className="flex items-start gap-3 bg-white rounded-xl border border-[#e6edf3] p-4"
                >
                  <span className="flex items-center justify-center h-9 w-9 rounded-full bg-[#eaf3fb] text-[#237cb5] shrink-0">
                    <Icon className="h-4 w-4" strokeWidth={2.4} />
                  </span>
                  <span className="text-[14.5px] text-[#0f1d2b] leading-[1.6] font-semibold mt-1">
                    {text}
                  </span>
                </li>
              ))}
            </ul>
          </article>

          {/* Section 2 : transparence pro */}
          <article className="bg-white rounded-2xl border border-[#e6edf3] p-6 sm:p-8 lg:p-10">
            <h2 className="text-[26px] sm:text-[30px] md:text-[36px] font-extrabold text-[#0f1d2b]">
              Des cartons professionnels, reconditionnés avec transparence
            </h2>
            <div className="mt-5 space-y-4 text-[15px] sm:text-base text-[#3a4a5a] leading-[1.8]">
              <p>
                Nos cartons sont à l'origine conçus pour les{" "}
                <strong>professionnels du déménagement</strong>. Ils sont donc plus résistants que des
                cartons classiques et peuvent supporter plusieurs manipulations, transports et
                déménagements lorsqu'ils restent en bon état.
              </p>
              <p>
                Les cartons reconditionnés que nous proposons ont déjà été <strong>utilisés une fois</strong>.
                Ils sont contrôlés avant d'être remis en vente, mais peuvent présenter quelques traces
                normales d'utilisation&nbsp;: marques de scotch, traces de marqueur, légères marques de
                pliage ou d'étiquette.
              </p>
              <p>
                Ces traces n'empêchent pas leur utilisation. L'objectif est de vous proposer des cartons
                solides à prix réduit, tout en donnant une seconde vie à du matériel encore utile.
              </p>
            </div>

            <h3 className="mt-8 text-base font-extrabold text-[#0f1d2b]">À retenir&nbsp;:</h3>
            <ul className="mt-4 space-y-2.5" data-testid="reconditionnes-points">
              {[
                "Cartons conçus pour un usage professionnel",
                "Déjà utilisés une fois, puis contrôlés",
                "Possibles traces de scotch ou de marqueur",
                "Prix réduit jusqu'à -40 %",
                "Solution économique et responsable",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-[15px] text-[#0f1d2b] leading-relaxed">
                  <CircleCheck className="h-5 w-5 mt-0.5 shrink-0 text-[#237cb5]" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </article>
        </div>
      </section>
    </div>
  );
};

export default CartonsReconditionnes;
